"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { devSectionAttr } from "@/lib/dev/section-attr";

import {
  SEO_PROCESS_ILLUSTRATIONS,
  type ProcessIllustration,
} from "./seo-process-illustrations";
import type { SeoPageCopy } from "./seo-page-copy";

type CcSeoProcessProps = {
  copy: SeoPageCopy["process"];
  /** One drawing per step; defaults to the SEO drawings. */
  illustrations?: ProcessIllustration[];
};

/** Restart a step's CSS animations and SMIL motion from the beginning, once. */
function playStep(step: HTMLElement) {
  step.dataset.anim = "";
  void step.offsetWidth; // flush so the same keyframes run again
  step.dataset.anim = "play";
  step.querySelectorAll<SVGAnimationElement>("animateMotion, animate").forEach((animation) => {
    try {
      animation.beginElement();
    } catch {
      // SMIL unsupported: the marker simply stays at its end point.
    }
  });
}

/**
 * Dark band with the heading above a four-step illustrated process. The
 * drawings are always visible; on entering view their outlines are drawn over
 * once, and each step has its own short animation (hover on desktop, once when
 * scrolled into view on touch screens).
 */
export function CcSeoProcess({
  copy,
  illustrations = SEO_PROCESS_ILLUSTRATIONS,
}: CcSeoProcessProps) {
  const listRef = useRef<HTMLOListElement | null>(null);
  // Mask IDs are created on the client only: the page HTML can contain a hidden
  // duplicate of the section, and server IDs would collide with it.
  const [maskIds, setMaskIds] = useState<(string | null)[]>(() => copy.steps.map(() => null));

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const steps = Array.from(list.querySelectorAll<HTMLElement>(".cc-seo-process__step"));
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) {
      list.dataset.motion = "off";
      return;
    }

    const suffix = Math.random().toString(36).slice(2, 8);
    const frame = requestAnimationFrame(() => {
      setMaskIds(steps.map((_, index) => `cc-seo-ill-mask-${suffix}-${index}`));
    });

    const canHover = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const cleanups: (() => void)[] = [() => cancelAnimationFrame(frame)];

    if (canHover) {
      // Desktop: draw all outlines once as the section arrives, staggered in CSS.
      const sectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry?.isIntersecting) return;
          sectionObserver.disconnect();
          list.dataset.intro = "play";
        },
        { threshold: 0.3 },
      );
      sectionObserver.observe(list);
      cleanups.push(() => sectionObserver.disconnect());

      steps.forEach((step) => {
        const onEnter = () => {
          step.dataset.hover = "on";
          playStep(step);
        };
        const onLeave = () => {
          step.dataset.hover = "off";
        };
        step.addEventListener("pointerenter", onEnter);
        step.addEventListener("pointerleave", onLeave);
        cleanups.push(() => {
          step.removeEventListener("pointerenter", onEnter);
          step.removeEventListener("pointerleave", onLeave);
        });
      });
    } else {
      // Touch: each step draws and plays its animation once when it's in view.
      const stepObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const step = entry.target as HTMLElement;
            stepObserver.unobserve(step);
            step.dataset.intro = "play";
            playStep(step);
          }
        },
        { threshold: 0.45 },
      );
      steps.forEach((step) => stepObserver.observe(step));
      cleanups.push(() => stepObserver.disconnect());
    }

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [copy.steps]);

  return (
    <section
      className="cc-seo-process"
      data-cc-nav="dark"
      aria-labelledby="cc-seo-process-title"
      {...devSectionAttr("seo-process")}
    >
      <div className="cc-seo-process__frame">
        <div className="cc-container">
          <header className="cc-seo-process__header">
            <CcSanjayaEyebrow theme="on-dark">{copy.eyebrow}</CcSanjayaEyebrow>
            <h2 id="cc-seo-process-title" className="cc-seo-heading cc-seo-heading--on-dark">
              {copy.title}
            </h2>
            <p className="cc-seo-process__intro">{copy.intro}</p>
          </header>

          <ol ref={listRef} className="cc-seo-process__steps">
            {copy.steps.map((step, index) => {
              const Illustration = illustrations[index % illustrations.length];
              return (
                <li
                  key={step.title}
                  className={`cc-seo-process__step cc-seo-process__step--${index + 1}`}
                  style={{ "--cc-step": index } as CSSProperties}
                >
                  <div className="cc-seo-process__meta">
                    <span className="cc-seo-process__number">
                      <span className="cc-seo-process__marker" aria-hidden="true" />
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="cc-seo-process__time">{step.time}</span>
                  </div>
                  <div className="cc-seo-process__figure">
                    <Illustration maskId={maskIds[index] ?? null} />
                  </div>
                  <h3 className="cc-seo-process__step-title">{step.title}</h3>
                  <p className="cc-seo-process__text">{step.text}</p>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
    </section>
  );
}
